import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Box, Link, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel } from '@mui/material'
import ComponentView, { IndexComponentData } from './ComponentView'
import commonActions from '../../actions/common.actions'
import { useDispatch, useSelector } from 'react-redux'
import { subscriptionTags } from '../../assets/constants'
import { selectCommonStore } from '../../selectors/common'
import { Security } from '../../proto/generated/security'
import { OverviewWidgetResponse_IndexComponent } from '../../proto/generated/overview-widget'
import { v4 as uuid } from 'uuid'
import websocketActions from '../../actions/websocket.actions'
import { RootState } from '../../store'
import { makeStyles } from 'tss-react/mui'

const useStyles = makeStyles()((theme) => ({
    container: {
        boxShadow: theme.shadows[0],
        width: '100%'
    },
    stickyHeader: {
        display: 'flex',
        width: '100%',
        alignItems: 'center',
        justifyContent: 'space-between',
        position: 'sticky',
        top: 0,
        zIndex: 10,
        padding: theme.spacing(2),
        boxShadow: theme.shadows[0],
        backgroundColor: theme.palette.background.paper
    },
    chipContainer: {
        alignItems: 'center',
        padding: theme.spacing(1)
    },
    iconPadding: {
        padding: 0
    }
}))

const IndexComponents = () => {
    const dispatch = useDispatch()
    const securityCodeToMetaData = useRef<Map<string, { prevClose: number; spi: number; weightage: number }>>(new Map())
    const [indexComponentData, setIndexComponentData] = useState<IndexComponentData[]>([])
    const isIndexComponentsVisible = useMemo(() => indexComponentData.length > 0, [indexComponentData])
    const initialVisibleLimit = useMemo(() => 10, [])
    const [limit, setLimit] = useState(initialVisibleLimit)
    const { overviewIndexComponentsSortedBy: indexComponentsSortedBy } = useSelector(selectCommonStore)
    const { indexComponents } = useSelector((state: RootState) => state.overviewStore)
    const { classes } = useStyles()
    const [securitiesToSubscribe, setSecuritiesToSubscribe] = useState<Security[]>([])
    const sortIndexComponents = useCallback((indexComponents: IndexComponentData[], criteria: string): IndexComponentData[] => {
        const sortByProperty = function (criteria: string) {
            return function (a: IndexComponentData, b: IndexComponentData) {
                switch (criteria) {
                    case 'ascending_symbol': {
                        return a.security!!.symbol!!.localeCompare(b.security!!.symbol!!)
                    }
                    case 'descending_symbol': {
                        return b.security!!.symbol!!.localeCompare(a.security!!.symbol!!)
                    }
                    case 'descending_change': {
                        return Math.abs(b.changePercentage ?? 0) - Math.abs(a.changePercentage ?? 0)
                    }
                    case 'ascending_change': {
                        return Math.abs(a.changePercentage ?? 0) - Math.abs(b.changePercentage ?? 0)
                    }
                    case 'descending_contribution': {
                        return Math.abs(b.contribution ?? 0) === Math.abs(a.contribution ?? 0)
                            ? Math.abs(b.weightage ?? 0) - Math.abs(a.weightage ?? 0)
                            : Math.abs(b.contribution ?? 0) - Math.abs(a.contribution ?? 0)
                    }
                    case 'ascending_contribution': {
                        return Math.abs(b.contribution ?? 0) === Math.abs(a.contribution ?? 0)
                            ? Math.abs(a.weightage ?? 0) - Math.abs(b.weightage ?? 0)
                            : Math.abs(a.contribution ?? 0) - Math.abs(b.contribution ?? 0)
                    }
                    case 'descending_weightage': {
                        return Math.abs(b.weightage ?? 0) - Math.abs(a.weightage ?? 0)
                    }
                    case 'ascending_weightage': {
                        return Math.abs(a.weightage ?? 0) - Math.abs(b.weightage ?? 0)
                    }
                    case 'ascending_ltp': {
                        return (a.ltp ?? 0) - (b.ltp ?? 0)
                    }
                    case 'descending_ltp': {
                        return (b.ltp ?? 0) - (a.ltp ?? 0)
                    }
                    default: {
                        return Math.abs(b.contribution ?? 0) - Math.abs(a.contribution ?? 0)
                    }
                }
            }
        }
        return criteria ? [...indexComponents].sort(sortByProperty(criteria)) : indexComponents
    }, [])

    useEffect(() => {
        websocketActions.setWebsocketSubscribeList(dispatch, {
            orderBookSubscriptions: [
                {
                    tag: 'indexComponentOrderBook',
                    securities: securitiesToSubscribe.map((sec) => ({
                        exchange: sec.exchange,
                        code: sec.code
                    })),
                    topBidAskOnly: false
                }
            ]
        })
    }, [securitiesToSubscribe])

    const subscribeOrderBook = useCallback(
        (security: Security, subscribe: boolean) => {
            if (subscribe) {
                setSecuritiesToSubscribe([...securitiesToSubscribe, security])
            } else {
                setSecuritiesToSubscribe(securitiesToSubscribe.filter((listSecurity) => listSecurity.code !== security.code))
            }
        },
        [securitiesToSubscribe]
    )

    const updateIndexComponentsSortCriteria = useCallback(
        (criteria: string) => {
            commonActions.setOverviewIndexComponentsSortedBy(dispatch, criteria)
        },
        [dispatch]
    )

    const onIndexSortMenuClick = useCallback(
        (value: string) => {
            updateIndexComponentsSortCriteria(value)
        },
        [updateIndexComponentsSortCriteria]
    )

    useEffect(() => {
        const newIndexComponentData: IndexComponentData[] = []
        const indexSecurities: Security[] = []
        indexComponents.forEach((component: OverviewWidgetResponse_IndexComponent) => {
            securityCodeToMetaData.current.set(component.security?.code ?? '', {
                prevClose: component.previousClose,
                spi: component.spi,
                weightage: component.weightage
            })
            if (component.security)
                indexSecurities.push({
                    exchange: component.security.exchange,
                    code: component.security.code,
                    companyCode: component.security.companyCode
                })
            const prevClose = component.previousClose
            const change = prevClose !== 0 ? component.ltp - prevClose : null
            const changePercentage = prevClose !== 0 ? (change! / prevClose) * 100 : null
            const contribution = change ? component.spi * change : null
            newIndexComponentData.push({
                id: uuid(),
                security: component.security!,
                ltp: component.ltp / (component.security!.priceMultiplier ?? 100),
                change: change ? change / (component.security!.priceMultiplier ?? 100) : null,
                changePercentage: changePercentage,
                contribution: contribution ? contribution / (component.security!.priceMultiplier ?? 100) : null,
                weightage: component.weightage,
                previousClose: component.previousClose
            })
        })
        setIndexComponentData(sortIndexComponents(newIndexComponentData, indexComponentsSortedBy))
        websocketActions.setWebsocketSubscribeList(dispatch, {
            securitySnapshotSubscriptions: [
                {
                    tag: subscriptionTags.INDEX_COMPONENT,
                    securities: indexSecurities
                }
            ]
        })
    }, [dispatch, indexComponents, indexComponentsSortedBy, sortIndexComponents])

    const onChipClick = useCallback(
        (limit: any) => {
            setLimit(limit === indexComponentData?.length ? initialVisibleLimit : indexComponentData?.length)
        },
        [indexComponentData, initialVisibleLimit]
    )

    return isIndexComponentsVisible ? (
        <Box className={classes.container}>
            {/* <Paper className={`${classes.stickyHeader} sticky`}>
                <Typography variant="body2">
                    Index Components
                </Typography>
                <PopupState parentPopupState={undefined} variant="popover" popupId="security-overview-popup-menu"
                            disableAutoFocus={false}>
                    {(popupState) => (
                        <React.Fragment>
                            <Button
                                variant="text"
                                color="inherit"
                                {...bindTrigger(popupState)}
                                size="small"
                                endIcon={
                                    (indexSortMenu.find((menuItem) => menuItem.value === indexComponentsSortedBy)
                                        ?? indexSortMenu[0]).icon
                                }
                            >
                                <Typography variant="body2">
                                    {(indexSortMenu.find((menuItem) => menuItem.value === indexComponentsSortedBy)
                                        ?? indexSortMenu[0]).label}
                                </Typography>
                            </Button>
                            <Menu {...bindMenu(popupState)} onClick={popupState.close}>
                                {indexSortMenu.map((menuItem) => (
                                    <MenuItem
                                        key={menuItem.label}
                                        selected={menuItem.value === indexComponentsSortedBy}
                                        onClick={() => onIndexSortMenuClick(menuItem.value)}
                                    >
                                        <ListItemText>{menuItem.label}</ListItemText>
                                        <ListItemIcon className={classes.iconPadding} sx={{padding: 0, minWidth: 0}}>
                                            {menuItem.icon}
                                        </ListItemIcon>
                                    </MenuItem>
                                ))}
                            </Menu>
                        </React.Fragment>
                    )}
                </PopupState>
            </Paper> */}

            <TableContainer style={{ overflowX: 'hidden', width: '100%', overflow: 'auto' }}>
                <Table
                    stickyHeader
                    style={{ borderCollapse: 'separate', fontSize: 13 }}
                    sx={{
                        tableLayout: 'fixed'
                    }}
                    className={'font-feature-num'}
                >
                    <TableHead>
                        <TableRow sx={{ height: 32, borderBottomColor: (theme) => theme.palette.divider }}>
                            {/* Components Column */}
                            <TableCell
                                width="36%"
                                sx={{ py: 0.5, px: 1 }}
                                sortDirection={indexComponentsSortedBy === 'ascending_symbol' ? 'asc' : indexComponentsSortedBy === 'descending_symbol' ? 'desc' : false}
                            >
                                <TableSortLabel
                                    active={indexComponentsSortedBy === 'ascending_symbol' || indexComponentsSortedBy === 'descending_symbol'}
                                    direction={indexComponentsSortedBy === 'ascending_symbol' ? 'asc' : indexComponentsSortedBy === 'descending_symbol' ? 'desc' : 'asc'}
                                    onClick={() => {
                                        let next: string
                                        if (indexComponentsSortedBy === 'ascending_symbol') next = 'descending_symbol'
                                        else if (indexComponentsSortedBy === 'descending_symbol') next = ''
                                        else next = 'ascending_symbol'
                                        onIndexSortMenuClick(next)
                                    }}
                                >
                                    Components
                                </TableSortLabel>
                            </TableCell>
                            {/* Contribution Column */}
                            <TableCell
                                width="22%"
                                align="right"
                                sx={{ py: 0.5, px: 1, position: 'relative' }}
                                sortDirection={indexComponentsSortedBy === 'ascending_contribution' ? 'asc' : indexComponentsSortedBy === 'descending_contribution' ? 'desc' : false}
                            >
                                <TableSortLabel
                                    sx={{ width: '100%' }}
                                    active={indexComponentsSortedBy === 'ascending_contribution' || indexComponentsSortedBy === 'descending_contribution'}
                                    direction={indexComponentsSortedBy === 'ascending_contribution' ? 'asc' : indexComponentsSortedBy === 'descending_contribution' ? 'desc' : 'asc'}
                                    onClick={() => {
                                        let next: string
                                        if (indexComponentsSortedBy === 'ascending_contribution') next = 'descending_contribution'
                                        else if (indexComponentsSortedBy === 'descending_contribution') next = ''
                                        else next = 'ascending_contribution'
                                        onIndexSortMenuClick(next)
                                    }}
                                >
                                    Contribution
                                </TableSortLabel>
                            </TableCell>
                            {/* Chg% Column */}
                            <TableCell
                                width="20%"
                                align="right"
                                sx={{ py: 0.5, px: 1 }}
                                sortDirection={indexComponentsSortedBy === 'ascending_change' ? 'asc' : indexComponentsSortedBy === 'descending_change' ? 'desc' : false}
                            >
                                <TableSortLabel
                                    active={indexComponentsSortedBy === 'ascending_change' || indexComponentsSortedBy === 'descending_change'}
                                    direction={indexComponentsSortedBy === 'ascending_change' ? 'asc' : indexComponentsSortedBy === 'descending_change' ? 'desc' : 'asc'}
                                    onClick={() => {
                                        let next: string
                                        if (indexComponentsSortedBy === 'ascending_change') next = 'descending_change'
                                        else if (indexComponentsSortedBy === 'descending_change') next = ''
                                        else next = 'ascending_change'
                                        onIndexSortMenuClick(next)
                                    }}
                                >
                                    Chg%
                                </TableSortLabel>
                            </TableCell>
                            {/* LTP Column */}
                            <TableCell
                                width="22%"
                                align="right"
                                sx={{ py: 0.5, px: 1 }}
                                sortDirection={indexComponentsSortedBy === 'ascending_ltp' ? 'asc' : indexComponentsSortedBy === 'descending_ltp' ? 'desc' : false}
                            >
                                <TableSortLabel
                                    active={indexComponentsSortedBy === 'ascending_ltp' || indexComponentsSortedBy === 'descending_ltp'}
                                    direction={indexComponentsSortedBy === 'ascending_ltp' ? 'asc' : indexComponentsSortedBy === 'descending_ltp' ? 'desc' : 'asc'}
                                    onClick={() => {
                                        let next: string
                                        if (indexComponentsSortedBy === 'ascending_ltp') next = 'descending_ltp'
                                        else if (indexComponentsSortedBy === 'descending_ltp') next = ''
                                        else next = 'ascending_ltp'
                                        onIndexSortMenuClick(next)
                                    }}
                                >
                                    LTP
                                </TableSortLabel>
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {indexComponentData.slice(0, limit).map((indexComponent) => (
                            <ComponentView key={indexComponent.security?.code} component={indexComponent} subscribeSecurity={subscribeOrderBook} />
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <Stack direction="row-reverse" spacing={1} sx={{ m: 0.5 }}>
                <Link color={"primary"} underline="hover" data-event={limit === indexComponentData.length ? 'View Less Clicked' : 'View More Clicked'} onClick={() => onChipClick(limit)} variant={'caption'}>
                    {limit === indexComponentData.length ? `show only top ${initialVisibleLimit} components` : `show all ${indexComponentData.length} components`}
                </Link>
            </Stack>
        </Box>
    ) : null
}

export default memo(IndexComponents)
